import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MappingCountryMarkets } from './useMappingCountryMarkets';

export interface MarketRateOptions {
    options: SelectMultiChoicelistOption<number>[];
    /** Labels for the currently selected ids, including ones no longer offered by the market. */
    selectedOptions: SelectMultiChoicelistOption<number>[];
    /** Selected ids that no longer exist in Shopify — flagged "- REMOVED" in `selectedOptions`. */
    removedValues: number[];
}

/**
 * Resolves a country's selected rate ids to labels.
 *
 * A rate saved earlier but since deleted from the market isn't among `options` any more. Its label
 * falls back to the name the GET mapping stored alongside it, flagged "- REMOVED", and its id is
 * reported in `removedValues` so the caller can tell the merchant. Nothing is flagged while the rates
 * failed to load (`hasOptionsError`), since then every saved rate would look removed.
 */
export const useMarketRateOptions = (
    country: MappingCountryMarkets,
    values: number[],
    options: SelectMultiChoicelistOption<number>[],
    hasOptionsError = false
): MarketRateOptions => {
    const { t } = useTranslation();

    const savedNames = useMemo(() => {
        const byId = new Map<number, string>();

        country.mapping.providerRates.forEach((rates) => {
            rates.shippingRates.forEach((rate) => {
                if (rate.name) byId.set(rate.id, rate.name);
            });
        });

        return byId;
    }, [country.mapping]);

    const removedValues = useMemo(() => {
        if (hasOptionsError) return [];

        const offered = new Set(options.map((option) => option.value));

        return values.filter((value) => !offered.has(value));
    }, [values, options, hasOptionsError]);

    const selectedOptions = useMemo(() => {
        const labels = new Map(options.map((option) => [option.value, option.label]));
        const removed = new Set(removedValues);

        return values.map((value) => {
            const name = labels.get(value) ?? savedNames.get(value) ?? `#${value}`;

            return {
                value,
                label: removed.has(value)
                    ? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.REMOVED_RATE', { name })
                    : name,
            };
        });
    }, [values, options, savedNames, removedValues, t]);

    return { options, selectedOptions, removedValues };
};
